import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { scorofunLockup, scorofunMark } from '@scoreboard/design-tokens';

// Composants maison sobres, 100 % tokens (doc 05 §4.4) — pas de kit UI lourd.

export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}): React.ReactElement {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
      .filter((v): v is string => v !== null)
      .join(' ') || undefined;
  return (
    <div className={`field${error !== undefined ? ' field--invalid' : ''}`}>
      {/* label toujours visible : jamais de placeholder-label (doc 05 §8) */}
      <label htmlFor={id}>{label}</label>
      {children({ id, describedBy })}
      {hint !== undefined ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error !== undefined ? (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

/** Boîte de dialogue accessible : focus piégé, Échap ferme, titre relié par aria. */
export function Dialog({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
    )?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables === undefined || focusables.length === 0) {
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previous?.focus();
    };
  }, [onClose]);

  return (
    <div className="dialog" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog__panel" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panelRef}>
        <div className="row row--between">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
        {children}
        {footer !== undefined ? <div className="row row--between">{footer}</div> : null}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Dialog title={title} onClose={onClose}>
      <p className="muted">{body}</p>
      <div className="row row--between">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className={`btn ${danger === true ? 'btn--danger' : 'btn--primary'}`}
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          {confirmLabel ?? t('common.confirm')}
        </button>
      </div>
    </Dialog>
  );
}

/** Message transitoire non bloquant — jamais de modale pendant le scoring (doc 00 §6.3.4). */
export function Toast({ message }: { message: string }): React.ReactElement {
  return (
    <div className="toast" role="status">
      {message}
    </div>
  );
}

/** Règle des 3 budgets (doc 05 §7) : rien < 400 ms, squelette, puis texte honnête. */
export function Loading({ label }: { label?: string }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <p className="muted" role="status" aria-live="polite">
      {label ?? t('common.loading')}
    </p>
  );
}

export function ErrorMessage({ message }: { message: string }): React.ReactElement {
  return (
    <p className="field__error" role="alert">
      {message}
    </p>
  );
}

// --- Marque ----------------------------------------------------------------
//
// Le logo n'est PAS un fichier importé par chaque app : c'est le rendu des tracés de
// `@scoreboard/design-tokens` (symbole « +1 », wordmark « Rondeur », pastille au
// dégradé de la signature c). Une seule source, donc une seule marque à changer.
//
// Les SVG sont composés une fois et mémoïsés — PARESSEUSEMENT, et c'est la raison
// d'être de `once()`. Les calculer au chargement du module rendrait `scorofunLockup`
// « utilisé » aux yeux du bundler : la webapp animateur, qui n'affiche que la pastille,
// embarquerait quand même les 7 ko de contours du wordmark. Ici, une app qui n'appelle
// pas `BrandLockup` ne les voit jamais.
function once(build: () => string): () => string {
  let svg: string | null = null;
  return () => (svg ??= build());
}

const markSvg = once(() => scorofunMark({ idSuffix: 'ui' }));
const lockupLight = once(() => scorofunLockup({ theme: 'light', idSuffix: 'ui-l' }));
const lockupDark = once(() => scorofunLockup({ theme: 'dark', idSuffix: 'ui-d' }));

/**
 * La marque réduite — pastille « +1 » seule, carrée. À utiliser partout où le nom
 * « Scorofun » est déjà écrit à côté (barre d'app), pour ne pas le dire deux fois.
 */
export function BrandMark({
  size = 26,
  className,
}: {
  /** Côté de la pastille en pixels. */
  size?: number;
  className?: string;
}): React.ReactElement {
  return (
    <span
      className={className === undefined ? 'brand-mark' : `brand-mark ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: markSvg() }}
    />
  );
}

/**
 * Le lockup complet — pastille + « scorofun ». Réservé aux écrans où la marque est le
 * sujet (connexion), jamais en compagnie du nom écrit.
 *
 * L'encre du mot suit le thème : le composant rend les DEUX versions et le CSS masque
 * celle qui ne correspond pas à `[data-theme]`. Lire le thème en JS ferait clignoter le
 * logo au premier rendu, et le back-office bascule de thème sans recharger.
 */
export function BrandLockup({
  height = 40,
  className,
}: {
  /** Hauteur de la pastille en pixels ; la largeur suit. */
  height?: number;
  className?: string;
}): React.ReactElement {
  return (
    <span
      className={className === undefined ? 'brand-lockup' : `brand-lockup ${className}`}
      style={{ height }}
      role="img"
      aria-label="Scorofun"
    >
      <span
        className="brand-lockup__light"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: lockupLight() }}
      />
      <span
        className="brand-lockup__dark"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: lockupDark() }}
      />
    </span>
  );
}
